/**
 * Reports Page
 *
 * List of radiology reports with filters
 */

import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  FileText,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
} from 'lucide-react'

import {
  useReports,
  formatReportDate,
  getReportStatusVariant,
} from '@/application/reports'
import type { ReportFilters } from '@/domain/report'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { REPORT_STATUS_LABELS, PAGINATION, MODALITY_LABELS, type ReportStatus, type Modality } from '@/config/constants'

// =============================================================================
// Component
// =============================================================================

export function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  // Filters state from URL
  const [filters, setFilters] = useState<ReportFilters>({
    status: (searchParams.get('status') as ReportStatus) || undefined,
    dateFrom: searchParams.get('dateFrom') || undefined,
    dateTo: searchParams.get('dateTo') || undefined,
    limit: PAGINATION.DEFAULT_LIMIT,
    offset: parseInt(searchParams.get('offset') || '0', 10),
  })

  const [showFilters, setShowFilters] = useState(false)

  // Fetch reports
  const { data, isLoading, isError, error } = useReports(filters)

  // Update filters and URL
  const updateFilters = (newFilters: Partial<ReportFilters>) => {
    const updated = { ...filters, ...newFilters, offset: 0 }
    setFilters(updated)

    // Update URL params
    const params = new URLSearchParams()
    if (updated.status) params.set('status', updated.status)
    if (updated.dateFrom) params.set('dateFrom', updated.dateFrom)
    if (updated.dateTo) params.set('dateTo', updated.dateTo)
    setSearchParams(params)
  }

  // Pagination
  const currentPage = Math.floor((filters.offset || 0) / (filters.limit || PAGINATION.DEFAULT_LIMIT)) + 1
  const totalPages = data ? Math.ceil(data.total / (filters.limit || PAGINATION.DEFAULT_LIMIT)) : 0

  const goToPage = (page: number) => {
    const offset = (page - 1) * (filters.limit || PAGINATION.DEFAULT_LIMIT)
    setFilters((prev) => ({ ...prev, offset }))
    setSearchParams((params) => {
      params.set('offset', offset.toString())
      return params
    })
  }

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      limit: PAGINATION.DEFAULT_LIMIT,
      offset: 0,
    })
    setSearchParams({})
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]">Reportes</h1>
          <p className="text-[var(--foreground-muted)]">
            Gestión de informes radiológicos
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filtros
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Ocultar' : 'Mostrar'}
            </Button>
          </div>
        </CardHeader>
        {showFilters && (
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Status */}
              <div>
                <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                  Estado
                </label>
                <select
                  className="w-full h-10 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
                  value={filters.status || ''}
                  onChange={(e) =>
                    updateFilters({ status: e.target.value as ReportStatus || undefined })
                  }
                >
                  <option value="">Todos</option>
                  {Object.entries(REPORT_STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                  Desde
                </label>
                <Input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => updateFilters({ dateFrom: e.target.value || undefined })}
                />
              </div>

              {/* Date To */}
              <div>
                <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                  Hasta
                </label>
                <Input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => updateFilters({ dateTo: e.target.value || undefined })}
                />
              </div>

              {/* Clear Filters */}
              <div className="flex items-end">
                <Button variant="outline" onClick={clearFilters} className="w-full">
                  Limpiar filtros
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Quick Stats - Clickable to filter */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card
          className="cursor-pointer hover:border-[var(--primary)] transition-colors"
          onClick={() => setFilters(prev => ({ ...prev, status: prev.status === 'DRAFT' ? undefined : 'DRAFT' }))}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${filters.status === 'DRAFT' ? 'bg-[var(--primary)] text-white' : 'bg-gray-100 dark:bg-gray-900/40'}`}>
                <FileText className={`h-5 w-5 ${filters.status === 'DRAFT' ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`} />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)]">Borradores</p>
                <p className="text-xl font-semibold">
                  {data?.reports.filter((r) => r.status === 'DRAFT').length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer hover:border-[var(--primary)] transition-colors"
          onClick={() => setFilters(prev => ({ ...prev, status: prev.status === 'PRELIMINARY' ? undefined : 'PRELIMINARY' }))}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${filters.status === 'PRELIMINARY' ? 'bg-[var(--primary)] text-white' : 'bg-yellow-100 dark:bg-yellow-900/40'}`}>
                <Clock className={`h-5 w-5 ${filters.status === 'PRELIMINARY' ? 'text-white' : 'text-yellow-600 dark:text-yellow-400'}`} />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)]">Por Firmar</p>
                <p className="text-xl font-semibold">
                  {data?.reports.filter((r) => r.status === 'PRELIMINARY').length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer hover:border-[var(--primary)] transition-colors"
          onClick={() => setFilters(prev => ({ ...prev, status: prev.status === 'FINAL' ? undefined : 'FINAL' }))}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${filters.status === 'FINAL' ? 'bg-[var(--primary)] text-white' : 'bg-green-100 dark:bg-green-900/40'}`}>
                <CheckCircle className={`h-5 w-5 ${filters.status === 'FINAL' ? 'text-white' : 'text-green-600 dark:text-green-400'}`} />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)]">Firmados</p>
                <p className="text-xl font-semibold">
                  {data?.reports.filter((r) => r.status === 'FINAL').length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer hover:border-[var(--primary)] transition-colors"
          onClick={() => setFilters(prev => ({ ...prev, status: undefined }))}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${!filters.status ? 'bg-[var(--primary)] text-white' : 'bg-blue-100 dark:bg-blue-900/40'}`}>
                <FileText className={`h-5 w-5 ${!filters.status ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`} />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)]">Total</p>
                <p className="text-xl font-semibold">{data?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Results */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <p className="text-red-500">{error?.message || 'Error al cargar reportes'}</p>
            </div>
          ) : !data?.reports.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-12 w-12 text-[var(--foreground-muted)] mb-4" />
              <p className="text-[var(--foreground-muted)]">No se encontraron reportes</p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--border)] bg-[var(--background-subtle)]">
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Paciente
                      </th>
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Estudio
                      </th>
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Estado
                      </th>
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Fecha
                      </th>
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Firmado
                      </th>
                      <th className="text-right p-4 font-medium text-[var(--foreground-muted)]">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reports.map((report) => (
                      <tr
                        key={report.id}
                        className="border-b border-[var(--border)] hover:bg-[var(--background-subtle)] transition-colors"
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-[var(--primary)]/10 flex items-center justify-center flex-shrink-0">
                              <User className="h-4 w-4 text-[var(--primary)]" />
                            </div>
                            <div className="min-w-0">
                              <Link
                                to={`/reports/${report.id}`}
                                className="font-medium text-[var(--foreground)] hover:text-[var(--primary)] block truncate"
                              >
                                {report.patientName}
                              </Link>
                              <p className="text-xs text-[var(--foreground-muted)]">
                                {report.patientDocumentType} {report.patientDocumentNumber}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="min-w-0">
                            <p className="font-medium text-[var(--foreground)] truncate">
                              {report.studyDescription || MODALITY_LABELS[report.modality as Modality] || report.modality}
                            </p>
                            <p className="text-xs text-[var(--foreground-muted)]">
                              {MODALITY_LABELS[report.modality as Modality] || report.modality} • {report.studyDate}
                            </p>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant={getReportStatusVariant(report.status)}>
                            {REPORT_STATUS_LABELS[report.status]}
                          </Badge>
                        </td>
                        <td className="p-4 text-sm text-[var(--foreground-muted)]">
                          {formatReportDate(report.createdAt)}
                        </td>
                        <td className="p-4 text-sm text-[var(--foreground-muted)]">
                          {report.signedAt ? formatReportDate(report.signedAt) : '-'}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Link to={`/reports/${report.id}`}>
                              <Button variant="ghost" size="sm" title="Ver detalle">
                                <Eye className="h-4 w-4" />
                              </Button>
                            </Link>
                            {(report.status === 'DRAFT' || report.status === 'PRELIMINARY') && (
                              <Link to={`/reports/${report.id}/edit`}>
                                <Button variant="ghost" size="sm" title="Editar">
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-[var(--border)]">
                  <p className="text-sm text-[var(--foreground-muted)]">
                    Mostrando {(filters.offset || 0) + 1} -{' '}
                    {Math.min(
                      (filters.offset || 0) + (filters.limit || PAGINATION.DEFAULT_LIMIT),
                      data.total
                    )}{' '}
                    de {data.total} reportes
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-[var(--foreground-muted)]">
                      Página {currentPage} de {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
